import { app } from '@microsoft/teams-js';

export type EnvironmentName = 'Teams' | 'Outlook' | 'Office' | 'Unknown';

export type DetectResult = {
    in365Environment: boolean;   // true if inside a Microsoft environment (Teams/Outlook/Office)
    environment: EnvironmentName;    // which environment, or 'Unknown'
};

type Options = { timeoutMs?: number };

let cachedPromise: Promise<DetectResult> | null = null;
let cachedResult: DetectResult | null = null;

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const t = setTimeout(() => reject(new Error('teams_init_timeout')), ms);
        p.then(
        (v) => {
            clearTimeout(t);
            resolve(v);
        },
        (e) => {
            clearTimeout(t);
            reject(e);
        },
        );
    });
}

function getAncestorOrigins(): string[] {
    // Chromium exposes DOMStringList at window.location.ancestorOrigins
    const a = (typeof window !== 'undefined'
        ? (window.location as any).ancestorOrigins
        : undefined) as { length: number; item(i: number): string | null } | undefined;

    if (!a || typeof a.length !== 'number' || typeof a.item !== 'function') return [];
    const out: string[] = [];
    for (let i = 0; i < a.length; i++) {
        const v = a.item(i);
        if (v) out.push(v);
    }
    return out;
}

/** Robust environment detection using Teams SDK, with fast memoization and safe fallbacks. */
export async function detectM365Environment(opts: Options = {}): Promise<DetectResult> {
    if (cachedResult) return cachedResult;
    if (cachedPromise) return cachedPromise;

    const timeoutMs = opts.timeoutMs ?? 1200;

    // SSR guard
    if (typeof window === 'undefined') {
        cachedResult = { in365Environment: false, environment: 'Unknown' };
        return cachedResult;
    }

    cachedPromise = (async (): Promise<DetectResult> => {
        // Try the official SDK first
        try {
        await withTimeout(app.initialize(), timeoutMs);
        const ctx = await withTimeout(app.getContext(), timeoutMs);
        const environmentName = (ctx as any)?.app?.environment?.name as EnvironmentName | undefined;
        const hostName = String((ctx as any)?.app?.host?.name ?? '');
        const normalizedHostName = hostName.toLowerCase();
        const environment: EnvironmentName = normalizedHostName.includes('teams')
            ? 'Teams'
            : normalizedHostName.includes('outlook')
                ? 'Outlook'
                : normalizedHostName.includes('office')
                    ? 'Office'
                    : environmentName === 'Teams' || environmentName === 'Outlook' || environmentName === 'Office'
                        ? environmentName
                        : 'Unknown';
        const result: DetectResult = { in365Environment: environment !== 'Unknown', environment };
        cachedResult = result;
        return result;
        } catch {
        // Fallback heuristics (don’t block app startup)
        const isFramed = window.parent !== window;
        const ua = navigator.userAgent || '';
        const origins = getAncestorOrigins();

        const teamsReferrer =
            document.referrer.includes('teams.microsoft.com') ||
            origins.some((o) => o.includes('teams.microsoft.com'));
        const likelyTeams = /Teams/i.test(ua) || (isFramed && teamsReferrer);

        const result: DetectResult = {
            in365Environment: likelyTeams,
            environment: likelyTeams ? 'Teams' : 'Unknown',
        };
        cachedResult = result;
        return result;
        }
    })();

    return cachedPromise;
}

/** Convenience: boolean check for Teams specifically (memoized). */
export async function isInTeams(timeoutMs?: number): Promise<boolean> {
    const r = await detectM365Environment({ timeoutMs });
    return r.environment === 'Teams';
}

/** Synchronous accessor after first detection (returns 'Unknown' if not detected yet). */
export function getCachedM365Environment(): DetectResult {
    return cachedResult ?? { in365Environment: false, environment: 'Unknown' };
}

/** Applies a destination supplied by a Teams tab deep link. */
export async function handleTeamsDeepLink(): Promise<void> {
    try {
        const context = await app.getContext();
        const destinationValue = context.page?.subPageId?.trim();
        if (!destinationValue) return;

        const destination = new URL(destinationValue, window.location.origin);
        if (destination.origin === window.location.origin) {
            const route = `${destination.pathname}${destination.search}${destination.hash}`;
            if (route !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
                window.location.replace(route);
            }
            return;
        }

        await app.openLink(destination.href);
    } catch (error) {
        console.error('Failed to handle Teams deep link', error);
    }
}
